import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useBlogApi } from '../hooks/useBlog';
import { Calendar, User, ArrowRight, Tag } from 'lucide-react';

const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { getPublishedPosts, getCategories } = useBlogApi();

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadPosts();
  }, [page, selectedCategory]);

  const loadCategories = async () => {
    try {
      const cats = await getCategories();
      setCategories(cats);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadPosts = async () => {
    try {
      setLoading(true);
      const data = await getPublishedPosts(page, 9, selectedCategory);
      setPosts(data.posts);
      setTotalPages(data.pages);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      {/* Navigation */}
      <nav className="bg-[#F9F6F0] border-b border-[#D4D1C9]/30">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="font-heading text-xl md:text-2xl font-semibold text-[#2C4A3E] tracking-tight">
              Fairway<span className="text-[#B89A5A]"> Partners</span>
            </Link>
            <Link to="/" className="font-body text-sm text-[#6B6860] hover:text-[#2C4A3E]">
              ← Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Header */}
      <header className="py-16 md:py-24 border-b border-[#D4D1C9]">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <span className="font-body text-sm font-medium tracking-widest uppercase text-[#B89A5A]">
            Insights & Articles
          </span>
          <h1 
            data-testid="blog-heading"
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-[#1A1A18] mt-4"
          >
            The Fairway Blog
          </h1>
          <p className="font-body text-lg text-[#6B6860] mt-4 max-w-2xl">
            Expert insights on strategic finance, tax planning, and business growth from our team of qualified professionals.
          </p>
        </div>
      </header>

      {/* Categories Filter */}
      {categories.length > 0 && (
        <div className="border-b border-[#D4D1C9]">
          <div className="container mx-auto px-6 md:px-12 lg:px-24 py-4">
            <div className="flex items-center gap-4 overflow-x-auto pb-2">
              <button
                onClick={() => { setSelectedCategory(null); setPage(1); }}
                className={`font-body text-sm whitespace-nowrap px-4 py-2 transition-colors ${
                  !selectedCategory 
                    ? 'bg-[#2C4A3E] text-white' 
                    : 'text-[#6B6860] hover:text-[#2C4A3E]'
                }`}
              >
                All Posts
              </button>
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => { setSelectedCategory(cat); setPage(1); }}
                  className={`font-body text-sm whitespace-nowrap px-4 py-2 transition-colors ${
                    selectedCategory === cat 
                      ? 'bg-[#2C4A3E] text-white' 
                      : 'text-[#6B6860] hover:text-[#2C4A3E]'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Posts Grid */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          {loading ? (
            <div className="text-center py-12">
              <p className="font-body text-[#6B6860]">Loading articles...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <p className="font-body text-[#6B6860]">No articles published yet. Check back soon!</p>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.map((post) => (
                  <article 
                    key={post.id}
                    data-testid={`blog-post-card-${post.slug}`}
                    className="bg-white border border-[#D4D1C9] group hover:shadow-lg transition-shadow"
                  >
                    {post.featured_image && (
                      <Link to={`/blog/${post.slug}`}>
                        <img 
                          src={post.featured_image} 
                          alt={post.title}
                          className="w-full h-48 object-cover"
                        />
                      </Link>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="font-body text-xs font-medium text-[#B89A5A] uppercase tracking-wider">
                          {post.category}
                        </span>
                      </div>
                      <Link to={`/blog/${post.slug}`}>
                        <h2 className="font-heading text-xl font-medium text-[#1A1A18] mb-3 group-hover:text-[#2C4A3E] transition-colors">
                          {post.title}
                        </h2>
                      </Link>
                      <p className="font-body text-sm text-[#6B6860] mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between pt-4 border-t border-[#D4D1C9]">
                        <div className="flex items-center gap-4 font-body text-xs text-[#6B6860]">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {post.author}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(post.published_at || post.created_at).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                      </div>
                      <Link 
                        to={`/blog/${post.slug}`}
                        className="flex items-center gap-2 font-body text-sm font-medium text-[#2C4A3E] mt-4 group-hover:gap-3 transition-all"
                      >
                        Read More <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </article>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-12">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-10 h-10 font-body text-sm ${
                        page === p 
                          ? 'bg-[#2C4A3E] text-white' 
                          : 'bg-white border border-[#D4D1C9] text-[#6B6860] hover:border-[#2C4A3E]'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A1A18] py-12">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="font-heading text-xl font-semibold text-white tracking-tight">
              Fairway<span className="text-[#B89A5A]"> Partners</span>
            </div>
            <div className="flex gap-8">
              <Link to="/" className="font-body text-sm text-white/60 hover:text-white">Home</Link>
              <Link to="/blog" className="font-body text-sm text-white/60 hover:text-white">Blog</Link>
              <Link to="/#contact" className="font-body text-sm text-white/60 hover:text-white">Contact</Link>
            </div>
            <div className="font-body text-sm text-white/40">
              © {new Date().getFullYear()} Fairway Partners Limited
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogList;
